import { useEffect, useState, useCallback, useRef } from "react";
import { LoadPanel } from "devextreme-react";
import { TextBox } from "devextreme-react/text-box";
import HtmlEditor, { Item, Toolbar as HTMLToolbar } from "devextreme-react/html-editor";
import Button from "devextreme-react/button";
import Splitter, { Item as SplitterItem } from "devextreme-react/splitter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import notify from "devextreme/ui/notify";
import { useTranslation } from "react-i18next";
import { getCurrentUser } from "../../../api/auth";
import { CustomDataGrid } from "../CustomDataGrid";
import { statusCellRender } from "../../../shared/status-selectbox";
import {
    CannedResponse,
    getAllCannedResponses,
    createCannedResponse,
    updateCannedResponse,
    deleteCannedResponse
} from "../../../api/support";
import "./CannedResponsesPage.css";

const PRODUCT_OPTIONS_BASE = [
    { value: null, labelKey: 'all' },
    { value: 10, label: 'mobiOffice' },
    { value: 11, label: 'mobiSales' },
    { value: 12, label: 'mobiShop' },
    { value: 14, label: 'mobiRoute' },
];

const PRODUCT_TEXT: Record<number, string> = {
    10: "mobiOffice", 11: "mobiSales", 12: "mobiShop", 14: "mobiRoute",
};

type DetailTab = { ref: string; responseId: string; title: string };
const createTabId = () => `ctab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function CannedResponsesPage() {
    const { t } = useTranslation();
    const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([]);
    const [filteredResponses, setFilteredResponses] = useState<CannedResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    // Filter state
    const [statusFilter, setStatusFilter] = useState<string>("active");

    const PRODUCT_OPTIONS = PRODUCT_OPTIONS_BASE.map(opt => ({
        value: opt.value,
        label: opt.label || t(opt.labelKey!),
    }));

    // Tab state
    const [detailTabs, setDetailTabs] = useState<DetailTab[]>([]);
    const [activeTabId, setActiveTabId] = useState<string | null>(null);
    const [editingTabIds, setEditingTabIds] = useState<Set<string>>(new Set());
    const [splitTabId, setSplitTabId] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);

    // Collapse state
    const [bottomCollapsed, setBottomCollapsed] = useState(false);
    const [topCollapsed, setTopCollapsed] = useState(false);

    const toggleBottom = useCallback(() => {
        setBottomCollapsed(prev => !prev);
        setTopCollapsed(false);
    }, []);

    const toggleTop = useCallback(() => {
        setTopCollapsed(prev => !prev);
        setBottomCollapsed(false);
    }, []);

    // Context menu
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; response: CannedResponse } | null>(null);
    const contextMenuRef = useRef<HTMLDivElement>(null);

    const handleRowContextMenu = useCallback((e: React.MouseEvent, data: CannedResponse) => {
        e.preventDefault();
        setContextMenu({ x: e.clientX, y: e.clientY, response: data });
    }, []);

    useEffect(() => {
        if (!contextMenu) return;
        const close = (e: MouseEvent) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
                setContextMenu(null);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [contextMenu]);

    // Form states
    const [formTitle, setFormTitle] = useState<string>("");
    const [formContent, setFormContent] = useState<string>("");
    const [formCategory, setFormCategory] = useState<string>("");
    const [formProductId, setFormProductId] = useState<number | null>(null);

    const user = getCurrentUser();

    const fetchCannedResponses = async () => {
        setLoading(true);
        try {
            const data = await getAllCannedResponses();
            setCannedResponses(data);
        } catch (error) {
            console.error("Error fetching canned responses:", error);
            notify(t("cannedLoadError"), "error", 2000);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCannedResponses(); }, []);

    // Filter responses
    useEffect(() => {
        let filtered = [...cannedResponses];

        if (statusFilter === "active") {
            filtered = filtered.filter(r => r.is_active);
        } else if (statusFilter === "passive") {
            filtered = filtered.filter(r => !r.is_active);
        }

        setFilteredResponses(filtered);
    }, [cannedResponses, statusFilter]);

    // Active tab's response
    const activeTab = detailTabs.find(t => t.ref === activeTabId) || null;
    const splitTab = splitTabId ? detailTabs.find(t => t.ref === splitTabId) || null : null;
    const activeResponse = activeTab ? cannedResponses.find(r => r.ref === activeTab.responseId) || null : null;
    const splitResponse = splitTab ? cannedResponses.find(r => r.ref === splitTab.responseId) || null : null;
    const isActiveEditing = activeTabId ? editingTabIds.has(activeTabId) : false;

    const populateForm = (response: CannedResponse) => {
        setFormTitle(response.title);
        setFormContent(response.content);
        setFormCategory(response.category || "");
        setFormProductId(response.product_ref ?? null);
    };

    const resetForm = () => {
        setFormTitle("");
        setFormContent("");
        setFormCategory("");
        setFormProductId(null);
    };

    // Tab management
    const openInTab = useCallback((response: CannedResponse) => {
        setDetailTabs(prev => {
            const existing = prev.find(t => t.responseId === response.ref);
            if (existing) {
                setActiveTabId(existing.ref);
                return prev;
            }
            const tabId = createTabId();
            setActiveTabId(tabId);
            return [...prev, { ref: tabId, responseId: response.ref, title: response.title }];
        });
        setIsCreating(false);
    }, []);

    const closeTab = useCallback((tabId: string) => {
        setDetailTabs(prev => {
            if (prev.length <= 1) {
                setActiveTabId(null);
                return [];
            }
            const idx = prev.findIndex(t => t.ref === tabId);
            const next = prev.filter(t => t.ref !== tabId);
            if (activeTabId === tabId) {
                const fallback = next[Math.max(0, idx - 1)] || next[0];
                setActiveTabId(fallback?.ref || null);
            }
            return next;
        });
        setEditingTabIds(prev => {
            const next = new Set(prev);
            next.delete(tabId);
            return next;
        });
    }, [activeTabId]);

    const openInSplitView = useCallback((response: CannedResponse) => {
        setDetailTabs(prev => {
            const existing = prev.find(t => t.responseId === response.ref);
            if (existing) {
                if (existing.ref === activeTabId) return prev;
                setSplitTabId(existing.ref);
                return prev;
            }
            const tabId = createTabId();
            setSplitTabId(tabId);
            return [...prev, { ref: tabId, responseId: response.ref, title: response.title }];
        });
    }, [activeTabId]);

    const closeSplitPanel = useCallback(() => {
        setSplitTabId(null);
    }, []);

    const selectResponse = useCallback((data: CannedResponse) => {
        if (!activeTabId || detailTabs.length === 0) {
            openInTab(data);
            return;
        }
        // Update active tab content instead of opening new tab
        setDetailTabs(prev => prev.map(tab =>
            tab.ref === activeTabId
                ? { ...tab, responseId: data.ref, title: data.title }
                : tab
        ));
        populateForm(data);
    }, [activeTabId, detailTabs.length, openInTab]);

    const handleRowClick = (data: CannedResponse) => {
        selectResponse(data);
    };

    const handleFocusedRowChanged = useCallback((e: any) => {
        if (e?.row?.data) {
            selectResponse(e.row.data);
        }
    }, [selectResponse]);

    // Edit
    const startEdit = () => {
        if (activeTabId && activeResponse) {
            populateForm(activeResponse);
            setEditingTabIds(prev => new Set(prev).add(activeTabId));
        }
    };

    const cancelEdit = () => {
        if (activeTabId) {
            setEditingTabIds(prev => {
                const next = new Set(prev);
                next.delete(activeTabId);
                return next;
            });
            setIsCreating(false);
            if (activeResponse) populateForm(activeResponse);
            else resetForm();
        }
    };

    const startCreate = () => {
        resetForm();
        setIsCreating(true);
        // Create a temporary tab
        const tabId = createTabId();
        setDetailTabs(prev => [...prev, { ref: tabId, responseId: '__new__', title: t("newCannedResponse") }]);
        setActiveTabId(tabId);
        setEditingTabIds(prev => new Set(prev).add(tabId));
    };

    const handleSave = async () => {
        if (!formTitle.trim() || !formContent.trim()) {
            notify(t("titleAndContentRequired"), "warning", 2000);
            return;
        }

        setLoading(true);
        try {
            if (isCreating) {
                await createCannedResponse({
                    title: formTitle,
                    content: formContent,
                    category: formCategory || undefined,
                    product_ref: formProductId || undefined,
                    created_by: user?.id,
                });
                notify(t("cannedCreated"), "success", 2000);
                // Close the temp tab
                if (activeTabId) closeTab(activeTabId);
                setIsCreating(false);
            } else if (activeResponse) {
                await updateCannedResponse(activeResponse.ref, {
                    title: formTitle,
                    content: formContent,
                    category: formCategory || undefined,
                    product_ref: formProductId || undefined,
                });
                notify(t("cannedUpdated"), "success", 2000);
                // Update tab title
                setDetailTabs(prev => prev.map(t =>
                    t.ref === activeTabId ? { ...t, title: formTitle } : t
                ));
                if (activeTabId) {
                    setEditingTabIds(prev => {
                        const next = new Set(prev);
                        next.delete(activeTabId);
                        return next;
                    });
                }
            }

            await fetchCannedResponses();
        } catch (error) {
            console.error("Error saving canned response:", error);
            notify(t("operationFailed"), "error", 2000);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm(t("confirmDeleteCanned"))) return;

        setLoading(true);
        try {
            await deleteCannedResponse(id);
            notify(t("cannedDeleted"), "success", 2000);
            // Close tab if open
            const tab = detailTabs.find(t => t.responseId === id);
            if (tab) closeTab(tab.ref);
            fetchCannedResponses();
        } catch (error) {
            console.error("Error deleting canned response:", error);
            notify(t("deleteFailed"), "error", 2000);
        } finally {
            setLoading(false);
        }
    };

    // When active tab changes, populate form
    useEffect(() => {
        if (activeResponse) {
            populateForm(activeResponse);
        }
    }, [activeTabId]);

    const columns = [
        {
            dataField: "title", caption: t("title"), width: 200, alignment: "left" as const,
            cellRender: (cellData: any) => (
                <span style={{ color: 'var(--base-text-color)' }}>{cellData.value}</span>
            ),
        },
        {
            dataField: "category", caption: t("category"), width: 120, alignment: "left" as const,
            cellRender: (cellData: any) => (
                <span style={{ color: 'var(--base-text-color-alpha)' }}>{cellData.value || '—'}</span>
            ),
        },
        {
            dataField: "product_ref", caption: t("product"), width: 120, alignment: "left" as const,
            cellRender: (cellData: any) => (
                <span style={{ color: 'var(--base-text-color)' }}>{PRODUCT_TEXT[cellData.value] || t("all")}</span>
            ),
        },
        {
            dataField: "is_active", caption: t("status"), width: 100, alignment: "center" as const,
            cellRender: statusCellRender,
        },
        {
            dataField: "created_at", caption: t("createdAt"), width: 120, alignment: "right" as const,
            cellRender: (cellData: any) => (
                <span className="text-xs" style={{ color: 'var(--subtitle-text-color)' }}>
                    {new Date(cellData.value).toLocaleDateString('tr-TR')}
                </span>
            ),
        },
        { dataField: "", caption: "", width: undefined },
        {
            dataField: "_actions", caption: "", width: 50, alignment: "center" as const,
            allowFiltering: false, allowSorting: false,
            cellRender: (cellData: any) => (
                <button type="button" className="canned-action-btn canned-delete-btn" title={t("deleteBtn")}
                    onClick={(e) => { e.stopPropagation(); handleDelete(cellData.data.ref); }}>
                    <i className="dx-icon-trash" />
                </button>
            ),
        },
    ];

    const renderDetailPane = (response: CannedResponse | null, editable: boolean) => (
        <div className="canned-detail-content h-full overflow-auto">
            <div className="canned-detail-meta">
                <div className="canned-meta-item">
                    <span className="canned-meta-label">{t("title")}</span>
                    {editable && isActiveEditing ? (
                        <TextBox value={formTitle} onValueChanged={(e) => setFormTitle(e.value)} placeholder={t("title")} width={200} />
                    ) : (
                        <span className="canned-meta-value">{response?.title || '—'}</span>
                    )}
                </div>
                <div className="canned-meta-item">
                    <span className="canned-meta-label">{t("category")}</span>
                    {editable && isActiveEditing ? (
                        <TextBox value={formCategory} onValueChanged={(e) => setFormCategory(e.value)} placeholder={t("category")} width={150} />
                    ) : (
                        <span className="canned-meta-value">{response?.category || '—'}</span>
                    )}
                </div>
                <div className="canned-meta-item">
                    <span className="canned-meta-label">{t("product")}</span>
                    {editable && isActiveEditing ? (
                        <select className="canned-select" value={formProductId || ''}
                            onChange={(e) => setFormProductId(e.target.value ? Number(e.target.value) : null)}>
                            {PRODUCT_OPTIONS.map(opt => (
                                <option key={opt.value || 'null'} value={opt.value || ''}>{opt.label}</option>
                            ))}
                        </select>
                    ) : (
                        <span className="canned-meta-value">{PRODUCT_TEXT[response?.product_ref!] || t("all")}</span>
                    )}
                </div>
                <div className="canned-meta-item">
                    <span className="canned-meta-label">{t("status")}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{
                            width: '10px', height: '10px', borderRadius: '50%',
                            backgroundColor: (response?.is_active ?? true) ? '#03a9f4' : '#de8e8c',
                        }} />
                        <span style={{ color: (response?.is_active ?? true) ? '#03a9f4' : '#de8e8c' }}>
                            {(response?.is_active ?? true) ? t("active") : t("passive")}
                        </span>
                    </span>
                </div>
            </div>
            <div className="canned-detail-body">
                {editable && isActiveEditing ? (
                    <HtmlEditor height={300} value={formContent} onValueChanged={(e) => setFormContent(e.value)}>
                        <HTMLToolbar>
                            <Item name="undo" /><Item name="redo" /><Item name="separator" />
                            <Item name="bold" /><Item name="italic" /><Item name="underline" /><Item name="separator" />
                            <Item name="orderedList" /><Item name="bulletList" /><Item name="separator" />
                            <Item name="link" />
                        </HTMLToolbar>
                    </HtmlEditor>
                ) : (
                    <div className="canned-html-content"
                        dangerouslySetInnerHTML={{ __html: response?.content || '' }} />
                )}
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-full w-full" style={{ paddingBottom: 30 }}>
            <LoadPanel visible={loading} />

            <div className="canned-splitter-wrap flex-1 min-h-0">
                <Splitter orientation="vertical">
                    <SplitterItem size="40%" minSize="200px" collapsed={topCollapsed}>
                        <CustomDataGrid
                            data={filteredResponses}
                            columns={columns}
                            keyExpr="ref"
                            height="100%"
                            onRefresh={fetchCannedResponses}
                            onAddNew={startCreate}
                            onRowClick={handleRowClick}
                            onRowContextMenu={handleRowContextMenu}
                            focusedRowKey={activeTab?.responseId || null}
                            onFocusedRowChanged={handleFocusedRowChanged}
                            noDataText={t("noCannedFound")}
                            columnChooserEnabled={true}
                            filterRowVisible={false}
                            rowAlternationEnabled={false}
                            allowColumnReordering={true}
                            statusFilterText={statusFilter === "active" ? t("active") : statusFilter === "passive" ? t("passive") : t("all")}
                            statusFilterOptions={[
                                { id: 1, text: t("all") },
                                { id: 2, text: t("active") },
                                { id: 3, text: t("passive") },
                            ]}
                            onStatusFilter={(status: string) => {
                                if (status === t("active")) setStatusFilter("active");
                                else if (status === t("passive")) setStatusFilter("passive");
                                else setStatusFilter("all");
                            }}
                            additionalToolbarItems={[{
                                location: 'after',
                                render: () => (
                                    <button type="button" className="splitter-collapse-btn" title={t("closeBottomPane")} onClick={toggleBottom}>
                                        <FontAwesomeIcon icon={bottomCollapsed ? faChevronUp : faChevronDown} />
                                    </button>
                                ),
                            }]}
                        />
                    </SplitterItem>

                    <SplitterItem size="60%" minSize="200px" collapsed={bottomCollapsed}>
                        <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--background-color)', padding: '0 12px' }}>
                            {detailTabs.length > 0 ? (
                                <>
                                    {/* Tab Bar */}
                                    <div className="detail-header-tabs shrink-0">
                                        {detailTabs
                                            .filter(tab => tab.ref !== splitTabId)
                                            .map(tab => (
                                            <div
                                                key={tab.ref}
                                                className={`detail-header-tab ${tab.ref === activeTabId ? 'active' : ''}`}
                                                title={tab.title}
                                                onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); closeTab(tab.ref); } }}
                                            >
                                                <button type="button" className="detail-header-tab-label" onClick={() => setActiveTabId(tab.ref)}>
                                                    <i className="dx-icon-doc detail-header-tab-icon" />
                                                    <span className="detail-header-tab-title-text">{tab.title}</span>
                                                </button>
                                                <button type="button" className="detail-header-tab-close"
                                                    onClick={(e) => { e.stopPropagation(); closeTab(tab.ref); }} title={t("closeTab")}>
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                        {splitTab && (
                                            <div
                                                className={`detail-header-tab split`}
                                                title={splitTab.title}
                                            >
                                                <button type="button" className="detail-header-tab-label" onClick={() => {}}>
                                                    <span className="detail-header-tab-title-text">{splitTab.title}</span>
                                                </button>
                                                <button type="button" className="detail-header-tab-close"
                                                    onClick={(e) => { e.stopPropagation(); closeSplitPanel(); }} title={t("close")}>
                                                    ×
                                                </button>
                                            </div>
                                        )}
                                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                                            {activeResponse && !isActiveEditing && (
                                                <Button icon="edit" text={t("edit")} stylingMode="outlined" onClick={startEdit} />
                                            )}
                                            {isActiveEditing && (
                                                <>
                                                    <Button text={t("cancel")} stylingMode="text" onClick={cancelEdit} />
                                                    <Button text={t("save")} type="success" onClick={handleSave} disabled={loading} />
                                                </>
                                            )}
                                            <button type="button" className="splitter-collapse-btn" title="Ustu kapat/ac" onClick={toggleTop}>
                                                <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Tab Content */}
                                    <div className="flex-1 min-h-0">
                                        {splitTab && splitResponse && activeResponse ? (
                                            <Splitter height="100%" width="100%">
                                                <SplitterItem size="50%" minSize="320px">
                                                    {renderDetailPane(activeResponse, true)}
                                                </SplitterItem>
                                                <SplitterItem size="50%" minSize="320px">
                                                    {renderDetailPane(splitResponse, false)}
                                                </SplitterItem>
                                            </Splitter>
                                        ) : (activeResponse || isCreating) ? (
                                            renderDetailPane(activeResponse, true)
                                        ) : null}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="detail-header-tabs shrink-0">
                                        <button type="button" className="splitter-collapse-btn" style={{ marginLeft: 'auto' }}
                                            title={t("closeTopPane")} onClick={toggleTop}>
                                            <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-center flex-1" style={{ color: 'var(--subtitle-text-color)' }}>
                                        {t("selectCannedForDetail")}
                                    </div>
                                </>
                            )}
                        </div>
                    </SplitterItem>
                </Splitter>
            </div>

            {/* Context Menu */}
            {contextMenu && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} />
                    <div
                        ref={contextMenuRef}
                        className="fixed z-50 canned-context-menu"
                        style={{ left: contextMenu.x, top: contextMenu.y }}
                    >
                        <button
                            className="canned-context-menu-item"
                            onClick={() => { openInTab(contextMenu.response); setContextMenu(null); }}
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            {t("openInNewTab")}
                        </button>
                        <button
                            className="canned-context-menu-item"
                            onClick={() => { openInSplitView(contextMenu.response); setContextMenu(null); }}
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" /></svg>
                            {t("openInSplitView")}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
